import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OrderDetail from './pages/OrderDetail';
import CreateOrder from './pages/CreateOrder';
import EditOrder from './pages/EditOrder';
import Tasks from './pages/Tasks';
import MyAccount from './pages/MyAccount';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import AgreementsSettings from './pages/AgreementsSettings';
import ApplicationForms from './pages/ApplicationForms';
import PublicAgreementSign from './pages/PublicAgreementSign';
import PublicApplicationSign from './pages/PublicApplicationSign';
import FamilyPage from './pages/FamilyPage';
import Materials from './pages/Materials';
import UserManagement from './pages/UserManagement';
import ArchivedOrders from './pages/ArchivedOrders';
import ChatWidget from './pages/Messages';
import WorktopsDashboard from './pages/WorktopsDashboard';
import CreateWorktop from './pages/CreateWorktop';
import WorktopDetail from './pages/WorktopDetail';
import EditWorktop from './pages/EditWorktop';
import ApplyTrade from './pages/ApplyTrade';
import TradeDashboard from './pages/TradeDashboard';
import TradeQuoteRequest from './pages/TradeQuoteRequest';
import PublicWorktopQuote from './pages/PublicWorktopQuote';
import PublicHearthQuote from './pages/PublicHearthQuote';
import LandingPage from './pages/LandingPage';
import SaaSLanding from './pages/SaaSLanding';
import AcceptQuote from './pages/AcceptQuote';
import AcceptQuoteOption from './pages/AcceptQuoteOption';
import ApproveArtwork from './pages/ApproveArtwork';
import RejectArtwork from './pages/RejectArtwork';
import Stock from './pages/Stock';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import Companies from './pages/Companies';
import CompanySettings from './pages/CompanySettings';
import BulkMessages from './pages/BulkMessages';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import SetupMigration from './pages/SetupMigration';
import GroupOrdering from './pages/GroupOrdering';
import TrialBanner from './components/TrialBanner';
import DemoBanner from './components/DemoBanner';
import LandingPageSetup from './pages/LandingPageSetup';
import CompanyLandingPage from './pages/CompanyLandingPage';
import AnnouncementPopup from './components/AnnouncementPopup';
import SubscriptionPaywall from './components/SubscriptionPaywall';
import PendingArtworkBanner from './components/PendingArtworkBanner';
import DailyRemindersPopup from './components/DailyRemindersPopup';
import Removals from './pages/Removals';
import FinancialReport from './pages/FinancialReport';
import CorelIntegration from './pages/CorelIntegration';
import ClaudeConnector from './pages/ClaudeConnector';
import DesignerQueue from './pages/DesignerQueue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global Axios interceptor: Transform Pydantic validation errors (arrays) to strings
// This prevents React Error #31 when toast.error() receives objects/arrays
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        error.response.data.detail = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (typeof detail === 'object' && detail !== null) {
        error.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
      }
    }
    return Promise.reject(error);
  }
);

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [companyContext, setCompanyContext] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const [userRes, companyRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/company`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);
      setUser(userRes.data);
      if (companyRes) {
        setCompanyContext(companyRes.data);
      }
    } catch (error) {
      // Only logout on a genuine auth failure (401). Transient network errors,
      // 5xx responses, or timeouts must NOT end the user's session — they happen
      // briefly during platform restarts / pod cold starts and would otherwise
      // boot the user to the login page during normal use.
      const status = error?.response?.status;
      if (status === 401) {
        console.warn('Auth check returned 401 — logging out');
        logout();
      } else {
        console.warn('Auth check failed transiently (status=' + status + '), keeping session');
        // Leave token in place; user will retry on their next action.
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    
    // Fetch company context after login
    try {
      const companyRes = await axios.get(`${API}/auth/company`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      setCompanyContext(companyRes.data);
      
      // Setup demo orders if demo user
      if (userData.role === 'demo' || userData.is_demo) {
        try {
          await axios.post(`${API}/auth/demo-setup-orders`, {}, {
            headers: { Authorization: `Bearer ${userToken}` }
          });
        } catch (e) {
          console.log('Demo orders setup:', e.message);
        }
      }
    } catch (error) {
      console.error('Failed to fetch company context:', error);
    }
  };

  const logout = async () => {
    // Reset demo account data before logout
    if (user?.role === 'demo' || user?.is_demo) {
      try {
        console.log('Resetting demo account before logout...');
        const response = await axios.post(`${API}/api/auth/demo-reset`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Demo reset successful:', response.data);
      } catch (e) {
        console.error('Demo reset failed:', e.response?.data || e.message);
      }
    }
    
    setUser(null);
    setToken(null);
    setCompanyContext(null);
    localStorage.removeItem('token');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, API, companyContext }}>
      <ErrorBoundary>
        <div className="App">
          <BrowserRouter>
            <AppContent user={user} companyContext={companyContext} />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </ErrorBoundary>
    </AuthContext.Provider>
  );
}

// Separate component to use useLocation hook inside BrowserRouter
function AppContent({ user, companyContext }) {
  const location = useLocation();
  
  // Hide chat widget on public pages
  const publicRoutes = ['/accept-quote', '/accept-quote-option', '/approve-artwork', '/reject-artwork', '/quote', '/hearth-quote', '/company', '/agreements/sign', '/application/sign'];
  const isPublicPage = publicRoutes.some(route => location.pathname.startsWith(route));
  // Chat widget shows for:
  // 1. Set in Stone company (master company) - all users
  // 2. Premium subscription companies - all users (only chat within their own company)
  const isMasterCompany = companyContext?.is_master === true || user?.role === 'super_admin' || user?.company_id === 'set-in-stone';
  const isPremiumCompany = companyContext?.subscription_tier === 'premium';
  const showChat = user && user.role !== 'trade' && !isPublicPage && (isMasterCompany || isPremiumCompany);
  
  // Helper to check if user has admin-level access (super_admin, company_admin, admin, or demo)
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager' || user?.role === 'demo';
  const isSuperAdmin = user?.role === 'super_admin';
  // Set in Stone admins can access platform features
  const isSetInStoneAdmin = (user?.company_id === 'set-in-stone') && isAdmin;
  
  // Check if worktops feature is enabled for the company
  const worktopsEnabled = companyContext?.features?.worktops_enabled !== false;
  const stockEnabled = companyContext?.features?.stock_management_enabled !== false;
  
  // Don't show trial banner on public pages or for non-logged-in users
  const showTrialBanner = user && !isPublicPage && user.role !== 'trade' && user.role !== 'demo' && !user.is_demo;
  
  // Show demo banner for demo users
  const showDemoBanner = user && (user.role === 'demo' || user.is_demo);

  return (
    <>
      {showDemoBanner && <DemoBanner />}
      {showTrialBanner && <TrialBanner />}
      {user && !isPublicPage && user.role !== 'trade' && <PendingArtworkBanner />}
      {user && <AnnouncementPopup />}
      {user && <SubscriptionPaywall />}
      {user && !isPublicPage && user.role !== 'trade' && <DailyRemindersPopup />}
      <Routes>
        {/* Main landing page - Memorial Desk SaaS */}
        <Route path="/" element={!user ? <SaaSLanding /> : (user.role === 'trade' ? <Navigate to="/trade-dashboard" /> : <Navigate to="/dashboard" />)} />
        
        {/* Set in Stone Didcot worktops page */}
        <Route path="/setinstone" element={<LandingPage />} />
        
        {/* Setup/Migration page */}
        <Route path="/setup" element={<SetupMigration />} />
        
        <Route path="/login" element={!user ? <Login /> : (user.role === 'trade' ? <Navigate to="/trade-dashboard" /> : <Navigate to="/dashboard" />)} />
        <Route path="/apply-trade" element={!user ? <ApplyTrade /> : (user.role === 'trade' ? <Navigate to="/trade-dashboard" /> : <Navigate to="/dashboard" />)} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : (user.role === 'trade' ? <Navigate to="/trade-dashboard" /> : <Navigate to="/dashboard" />)} />
        <Route path="/reset-password" element={!user ? <ResetPassword /> : (user.role === 'trade' ? <Navigate to="/trade-dashboard" /> : <Navigate to="/dashboard" />)} />
        <Route path="/quote" element={<PublicWorktopQuote />} />
        <Route path="/quote/:companySlug" element={<PublicWorktopQuote />} />
        <Route path="/hearth-quote" element={<PublicHearthQuote />} />
        <Route path="/hearth-quote/:companySlug" element={<PublicHearthQuote />} />
        <Route path="/agreements/sign/:token" element={<PublicAgreementSign />} />
        <Route path="/application/sign/:token" element={<PublicApplicationSign />} />
        <Route path="/family/:token" element={<FamilyPage />} />
        <Route path="/trade-dashboard" element={user?.role === 'trade' ? <TradeDashboard /> : <Navigate to="/login" />} />
        <Route path="/trade-quote-request" element={user?.role === 'trade' ? <TradeQuoteRequest /> : <Navigate to="/login" />} />
        <Route path="/dashboard" element={user ? (user.role === 'trade' ? <Navigate to="/trade-dashboard" /> : <Dashboard />) : <Navigate to="/login" />} />
        <Route path="/my-account" element={user ? <MyAccount /> : <Navigate to="/login" />} />
        <Route path="/users" element={isAdmin ? <UserManagement /> : <Navigate to="/dashboard" />} />
        <Route path="/archived" element={isAdmin ? <ArchivedOrders /> : <Navigate to="/dashboard" />} />
        <Route path="/orders/new" element={(user?.role === 'staff' || user?.role === 'workshop' || isAdmin) ? <CreateOrder /> : <Navigate to="/dashboard" />} />
        <Route path="/edit-order/:orderId" element={isAdmin ? <EditOrder /> : <Navigate to="/dashboard" />} />
        <Route path="/orders/:orderId" element={user ? <OrderDetail /> : <Navigate to="/login" />} />
        <Route path="/tasks" element={(user?.role === 'staff' || user?.role === 'workshop' || isAdmin) ? <Tasks /> : <Navigate to="/dashboard" />} />
        <Route path="/settings" element={isAdmin ? <Settings /> : <Navigate to="/dashboard" />} />
        <Route path="/settings/agreements" element={isAdmin ? <AgreementsSettings /> : <Navigate to="/dashboard" />} />
        <Route path="/settings/application-forms" element={isAdmin ? <ApplicationForms /> : <Navigate to="/dashboard" />} />

        <Route path="/materials" element={isAdmin ? <Materials /> : <Navigate to="/dashboard" />} />
        <Route path="/stock" element={isAdmin && stockEnabled ? <Stock /> : <Navigate to="/dashboard" />} />
        
        {/* Group Ordering - available to all memorial companies */}
        <Route path="/group-ordering" element={isAdmin ? <GroupOrdering /> : <Navigate to="/dashboard" />} />

        {/* Designer Queue — only the platform designer (super_admin / memorials@) */}
        <Route path="/designer-queue" element={user?.role === 'super_admin' || user?.email === 'memorials@setinstonedidcot.co.uk' ? <DesignerQueue /> : <Navigate to="/dashboard" />} />

        {/* Worktop routes - only if feature is enabled */}
        <Route path="/worktops" element={isAdmin && worktopsEnabled ? <WorktopsDashboard /> : <Navigate to="/dashboard" />} />
        <Route path="/worktops/new" element={isAdmin && worktopsEnabled ? <CreateWorktop /> : <Navigate to="/dashboard" />} />
        <Route path="/worktops/:worktopId/edit" element={isAdmin && worktopsEnabled ? <EditWorktop /> : <Navigate to="/dashboard" />} />
        <Route path="/worktops/:worktopId" element={(isAdmin || user?.role === 'trade') && worktopsEnabled ? <WorktopDetail /> : <Navigate to="/dashboard" />} />
        
        {/* Super Admin routes */}
        <Route path="/platform-admin" element={(isSuperAdmin || isSetInStoneAdmin) ? <SuperAdminDashboard /> : <Navigate to="/dashboard" />} />
        <Route path="/companies" element={isSuperAdmin ? <Companies /> : <Navigate to="/dashboard" />} />
        
        {/* Company Admin routes */}
        <Route path="/company-settings" element={isAdmin ? <CompanySettings /> : <Navigate to="/dashboard" />} />
        <Route path="/bulk-messages" element={isAdmin ? <BulkMessages /> : <Navigate to="/dashboard" />} />
        <Route path="/landing-page-setup" element={isAdmin ? <LandingPageSetup /> : <Navigate to="/dashboard" />} />
        <Route path="/financial-report" element={(isAdmin && user?.role !== 'demo' && !user?.is_demo) ? <FinancialReport /> : <Navigate to="/dashboard" />} />
        <Route path="/corel-integration" element={(isAdmin && companyContext?.features?.corel_integration_enabled === true) ? <CorelIntegration /> : <Navigate to="/dashboard" />} />
        <Route path="/settings/claude" element={(isAdmin && isMasterCompany) ? <ClaudeConnector /> : <Navigate to="/dashboard" />} />
        
        {/* Public Company Landing Page */}
        <Route path="/company/:companySlug" element={<CompanyLandingPage />} />
        
        {/* Subscription routes */}
        <Route path="/subscription" element={isAdmin ? <Subscription /> : <Navigate to="/dashboard" />} />
        <Route path="/subscription/success" element={isAdmin ? <SubscriptionSuccess /> : <Navigate to="/dashboard" />} />
        <Route path="/subscription/cancel" element={isAdmin ? <SubscriptionCancel /> : <Navigate to="/dashboard" />} />
        
        {/* Public routes for quote acceptance */}
        <Route path="/accept-quote/:token" element={<AcceptQuote />} />
        <Route path="/accept-quote-option/:token" element={<AcceptQuoteOption />} />
        <Route path="/approve-artwork/:token" element={<ApproveArtwork />} />
        <Route path="/reject-artwork/:token" element={<RejectArtwork />} />
      </Routes>
      {showChat && <ChatWidget />}
    </>
  );
}

export default App;